import { parsePrivilegedRouteMap } from '@thoughtspot/blink-context';
import { AppLogoType } from '@thoughtspot/custom-style-service';
import { Vertical } from '@thoughtspot/radiant-react/widgets/layout';
import React, { useEffect } from 'react';
import { createHashRouter, Outlet, RouterProvider } from 'react-router-dom';
import { useFavicon } from 'react-use';
import { FLAGS, flags } from '/@services/system/config-service/flags-service';
import { useUpdateSessionInfo } from '/@services/system/login-service';
import {
    getUrlWithoutUTMParameters,
    replaceState,
    useSetupNavService,
} from '/@services/system/nav-service/nav-service';
import { PERF_EVENT,PERF_KEYS, setPerfLog } from '/@utils/perf-log';
import { apolloCachePolicyLoaderSync } from './apollo-client-policies';
import { SessionAwareAppRoot } from './components/session-aware-app-root';
import { useAppContext } from './contexts/global-app-context';
import { AuthenticatedRoutes } from './routing/authenticated-app-view';
import { UNAUTHENTICATED_ROUTE_OBJECTS } from './routing/core-routes';
import { NavActions } from './routing/nav-actions';
import { collectQueryParamEvent } from './services/metrics-service/metrics-service';
import { BlinkV2MetricsContextStates } from './services/system/blink-v2-metrics-context-states';
import { appPerformanceTracker } from './utils/app-perf.util';
import { PerformanceMarkers } from './utils/common-enum';
import { ErrorBoundary } from './utils/error-boundary/error-boundary';

// Lazy-load MetricsFloater so it does not impact the main bundle size
const LazyMetricsFloater = React.lazy(() =>
    import('./components/fe-metrics-floater/MetricsFloater').then(m => ({
        default: m.MetricsFloater,
    })),
);

declare const window: any;
const KPI_MONITOR = 'kpi_monitor';

const AppRoot: React.FC = () => {
    setPerfLog(PERF_KEYS.APP_ROOT, PERF_EVENT.START);
    const { customStylingService } = useAppContext();
    useFavicon(
        customStylingService.getConfig()?.getFaviconUrl(AppLogoType.DEFAULT),
    );
    const setup = useSetupNavService();
    const showMetricsFloater = flags.getValue(
        FLAGS.isMetricsFloaterEnabled?.name,
    );
    useEffect(() => setup(), []);
    useEffect(() => {
        // Logging mfeinfo to include in har file
        if (window.blink?.microfrontends) {
            fetch('/prism/mfeInfo', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(window.blink?.microfrontends),
            });
        }
        // Mark app mount time
        if (showMetricsFloater) {
            appPerformanceTracker.markAppMount();
            performance.measure(
                PerformanceMarkers.APP_MOUNT,
                PerformanceMarkers.APP_START_TIME,
                PerformanceMarkers.APP_MOUNTED,
            );
        }
    }, []);

    /**
     * Removes UTM source and UTM medium parameters from the URL if the UTM source is 'kpi_monitor'.
     * This ensures accurate mixpanel tracking of users who click the 'View KPI' button from the email.
     */
    const UTMSource = flags.getValue(FLAGS.utm_source.name);
    useEffect(() => {
        if (UTMSource === KPI_MONITOR) {
            const updatedURL = getUrlWithoutUTMParameters();
            replaceState(updatedURL);
        }
    }, [UTMSource]);

    const headerColor = customStylingService.getConfig()?.getAppPanelColor()
        ?.color;

    return (
        <Vertical grow vAlignContent="center">
            <NavActions />
            <Outlet />
            {showMetricsFloater && (
                <React.Suspense fallback={null}>
                    <LazyMetricsFloater headerColor={headerColor} />
                </React.Suspense>
            )}
        </Vertical>
    );
};


export const getMainRouteObject = () => {
    const SW_VERSION = localStorage?.getItem('sw-version');
    return parsePrivilegedRouteMap({
        Root: {
            element: SW_VERSION ? (
                <SessionAwareAppRoot onSessionUpdate={useUpdateSessionInfo}>
                    <AppRoot />
                </SessionAwareAppRoot>
            ) : <AppRoot />,
            children: {
                AuthenticatedRoutes,
                ...UNAUTHENTICATED_ROUTE_OBJECTS,
            },
            loader: async (options: any): Promise<void> => {
                setPerfLog(PERF_KEYS.APP_ROUTER_LOADER, PERF_EVENT.START);
                apolloCachePolicyLoaderSync();
                collectQueryParamEvent(options.request.url);
                setPerfLog(PERF_KEYS.APP_ROUTER_LOADER, PERF_EVENT.COMPLETE);
                return null;
            },
            shouldRevalidate: ({
                nextUrl,
                currentUrl,
            }: {
                nextUrl: URL;
                currentUrl: URL;
            }) => nextUrl.search !== currentUrl.search,
            errorElement: (
                <Vertical minHeight="100%" vAlignContent="center">
                    <ErrorBoundary />,
                </Vertical>
            ),
            pageContext: BlinkV2MetricsContextStates.DEFAULT_PAGE,
        },
    });
}
    
export const App: React.FC = () => {
    setPerfLog(PERF_KEYS.APP_ROUTER, PERF_EVENT.START);
    const hashRouter = createHashRouter(getMainRouteObject());
    return <RouterProvider router={hashRouter} />;
};
